'use client';

import React, { Suspense, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { Layout } from '@benzinga/core-ui';
import { MoneyBlocksLayout, MoneyPost } from '@benzinga/money';
import { PresetType } from '@benzinga/scanner-widget';
import { CalendarNewLayoutHeader } from '../calendars/components/CalendarNewLayoutHeader';
import { LayoutAbove } from './components/LayoutAbove';
import { getShortInterestBreadcrumbs, presetFromPathname, PRESET_TITLES } from './constants';
import type { AboutPageProps } from './data';

const DefaultSidebar = React.lazy(() =>
  import('../../../src/components/Sidebars/DefaultSidebar').then(module => ({ default: module.default })),
);

function useHeaderPropsFromUrl(initialPreset: PresetType) {
  const pathname = usePathname();
  return useMemo(() => {
    const preset = pathname?.startsWith('/short-interest') ? presetFromPathname(pathname) : initialPreset;
    return {
      preset,
      breadcrumbs: getShortInterestBreadcrumbs(preset),
      title: PRESET_TITLES[preset],
      description:
        preset === 'most-shorted'
          ? 'Stocks with the largest short interest positions. Screen by short interest, float short, and more.'
          : `Stocks with the ${preset === 'largest-increase' ? 'largest increase' : 'largest decrease'} in short interest.`,
      canonical: `https://www.benzinga.com/short-interest${preset ? `/${preset}` : ''}`,
    };
  }, [pathname, initialPreset]);
}

export function ShortInterestPageContent({ post, preset, scannerData }: AboutPageProps & { preset: PresetType }) {
  const { breadcrumbs, canonical, description, title, preset: urlPreset } = useHeaderPropsFromUrl(preset);

  return (
    <div className="calendar-page-layout-wrapper">
      <Layout
        className="calendar-page-layout calendar-page-layout--short-interest new-design"
        layoutAbove={
          <div className="relative mb-8 lg:mt-4">
            {post?.content_header && (
              <div className="mb-6">
                <MoneyBlocksLayout post={post.content_header} />
              </div>
            )}
            <LayoutAbove preset={urlPreset} scannerData={scannerData} />
          </div>
        }
        layoutBelow={post?.content_footer ? <MoneyBlocksLayout post={post.content_footer} /> : undefined}
        layoutHeader={
          <CalendarNewLayoutHeader
            breadcrumbs={breadcrumbs}
            canonical={canonical}
            description={description}
            showEdgeBadge={false}
            title={title}
          />
        }
        layoutHeaderOptions={{ full_width: true }}
        layoutMain={<MoneyPost post={post} />}
        layoutSidebar={
          <Suspense fallback={null}>
            <DefaultSidebar sidebar={post?.sidebar} />
          </Suspense>
        }
      />
    </div>
  );
}
